/**
 * Warm the connection to the invest app origin so a cold open doesn't pay DNS
 * lookup + TLS handshake on the critical path between the click and the iframe's
 * first byte. Called at button-bind / client-construction time (i.e. before the
 * user clicks), so the socket is already open when the popup/iframe navigates.
 *
 * Idempotent per origin and an SSR-safe no-op. Best-effort: if `<head>` isn't
 * ready or the origin is odd, it silently does nothing — this only ever speeds
 * things up, it must never break the flow.
 */
const preconnected = new Set<string>();

export function preconnect(origin: string): void {
  if (typeof document === 'undefined' || !document.head) return;
  if (preconnected.has(origin)) return;
  preconnected.add(origin);
  try {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);

    // dns-prefetch as a fallback for engines that ignore preconnect.
    const dns = document.createElement('link');
    dns.rel = 'dns-prefetch';
    dns.href = origin;
    document.head.appendChild(dns);
  } catch {
    // Non-fatal — a failed warm-up just means the connection opens at click.
  }
}
